import type { ProductFeatureDefinition } from "./product-feature-definition";

/**
 * Catalog entries for building and working lists, the contact database and enrichment
 * (PREC-4329). Each list source is its own feature, because the dashboard compares their adoption;
 * the sources are picked inside one list builder, so no route names them.
 */
export const LIST_PRODUCT_FEATURES = [
	{ key: "list-builder", label: "List builder", area: "Lists", surfaces: ["web", "mcp"] },
	{
		key: "list-table",
		label: "List table and filters",
		area: "Lists",
		surfaces: ["web", "mcp"],
	},
	{
		key: "list-settings",
		label: "List settings and runs",
		area: "Lists",
		surfaces: ["web", "mcp"],
	},
	{ key: "recurring-list", label: "Recurring list", area: "Lists", surfaces: ["web"] },
	{ key: "list-preview", label: "List preview", area: "Lists", surfaces: ["web"] },
	{ key: "lead-review", label: "Lead review", area: "Lists", surfaces: ["web", "mcp"] },
	{ key: "lead-panel", label: "Lead side panel", area: "Lists", surfaces: ["web", "extension"] },
	{ key: "custom-column", label: "Custom columns", area: "Lists", surfaces: ["web"] },
	{ key: "list-export", label: "List export", area: "Lists", surfaces: ["web", "mcp"] },
	{ key: "find-people", label: "Find People", area: "Lists", surfaces: ["web", "extension"] },
	{
		key: "smart-list",
		label: "Smart list",
		area: "Lists",
		surfaces: ["web", "mcp"],
		featureCode: "SMART_LIST",
	},
	{
		key: "scraping-request",
		label: "Event scraping request",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-manual-leads",
		label: "Hand-filled list",
		area: "Lists",
		surfaces: ["web", "extension", "mcp"],
	},
	{ key: "list-source-csv", label: "CSV upload list", area: "Lists", surfaces: ["web"] },
	{
		key: "list-source-sales-navigator",
		label: "Sales Navigator list",
		area: "Lists",
		surfaces: ["web", "extension", "mcp"],
	},
	{ key: "list-source-job-change", label: "Job-change list", area: "Lists", surfaces: ["web"] },
	{
		// ListSourceType.COMPANY_ENRICH: search, lookalike and new-in-role; the web calls it
		// "Lead Database".
		key: "list-source-company-enrich",
		label: "Lead database list",
		area: "Lists",
		surfaces: ["web", "mcp"],
	},
	{
		key: "list-source-linkedin-post",
		label: "LinkedIn post engagement list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-custom-event",
		label: "Event attendee list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-linkedin-event",
		label: "LinkedIn event list",
		area: "Lists",
		surfaces: ["web", "extension"],
	},
	{
		key: "list-source-linkedin-people-search",
		label: "LinkedIn people search list",
		area: "Lists",
		surfaces: ["web", "extension", "mcp"],
	},
	{
		key: "list-source-linkedin-company-activity",
		label: "LinkedIn company activity list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-profile-visits",
		label: "LinkedIn profile visitors list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-linkedin-connections",
		label: "LinkedIn connections list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-linkedin-profile",
		label: "LinkedIn profile list (legacy)",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-job-postings",
		label: "Job postings list",
		area: "Lists",
		surfaces: ["web", "mcp"],
	},
	{ key: "list-source-google-maps", label: "Google Maps list", area: "Lists", surfaces: ["web"] },
	{
		key: "list-source-funding-rounds",
		label: "Funding rounds list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "list-source-parallel-ai",
		label: "Parallel AI list",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		// ListSourceType.LEAD_DATABASE: display-only since COMPANY_ENRICH took over the flow.
		key: "list-source-lead-database",
		label: "Lead database list (legacy)",
		area: "Lists",
		surfaces: ["web"],
	},
	{
		key: "contacts",
		label: "Contacts",
		area: "Contacts",
		surfaces: ["web", "mcp", "extension"],
		featureCode: "CONTACT",
	},
	{
		key: "contact-companies",
		label: "Companies",
		area: "Contacts",
		surfaces: ["web"],
		featureCode: "CONTACT",
	},
	{
		key: "global-search",
		label: "Global search and enrich",
		area: "Contacts",
		surfaces: ["web"],
	},
	{
		key: "blacklist",
		label: "Blacklist",
		area: "Contacts",
		surfaces: ["web", "mcp"],
		featureCode: "CONTACT",
	},
	{
		key: "competitors",
		label: "Competitors",
		area: "Contacts",
		surfaces: ["web"],
		featureCode: "CONTACT",
	},
	{
		key: "contact-lookup",
		label: "Email and phone lookup",
		area: "Enrichment",
		surfaces: ["web", "extension", "mcp"],
	},
	{
		key: "find-person",
		label: "Find person at company",
		area: "Enrichment",
		surfaces: ["web", "api"],
	},
	{
		key: "resolve-linkedin-url",
		label: "Resolve LinkedIn URL",
		area: "Enrichment",
		surfaces: ["web", "api"],
	},
	{
		key: "company-contact-lookup",
		label: "Company email and phone lookup",
		area: "Enrichment",
		surfaces: ["web"],
	},
	{
		key: "find-title-at-company",
		label: "Find title at company",
		area: "Enrichment",
		surfaces: ["web"],
	},
	{ key: "ai-column", label: "AI column", area: "Enrichment", surfaces: ["web"] },
	{
		key: "re-enrichment",
		label: "Re-enrichment",
		area: "Enrichment",
		surfaces: ["web", "extension"],
	},
	{
		key: "deep-research",
		label: "Deep research",
		area: "Enrichment",
		surfaces: ["web", "extension", "mcp"],
		featureCode: "DEEP_RESEARCH",
	},
	{
		key: "connection-enrichment",
		label: "LinkedIn connections enrichment",
		area: "Enrichment",
		surfaces: ["web"],
		featureCode: "LI_CONNECTIONS_ENRICHMENT",
	},
] as const satisfies readonly ProductFeatureDefinition[];
